// ╔══════════════════════════════════════════════════════════════════╗
// ║                   MARKET SIMULATOR PANEL                         ║
// ║  Simple controls to start/stop market updates                    ║
// ║  Individual simulation settings are now per-security             ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Play,
  Pause,
  Activity,
  RefreshCw,
  Loader2,
  Check,
  X
} from 'lucide-react'
import { MarketSimulatorAPI } from '../../services/adminApi'

export default function MarketSimulatorPanel({ onPriceUpdate }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const fetchStatus = useCallback(async () => {
    try {
      const res = await MarketSimulatorAPI.getStatus()
      if (res.success) {
        setStatus(res.data)
      }
    } catch (err) {
      console.error('Error fetching simulator status:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 5000)
    return () => clearInterval(interval)
  }, [fetchStatus])

  const handleStart = async () => {
    setActionLoading(true)
    setError(null)
    try {
      const res = await MarketSimulatorAPI.start({})
      if (res.success) {
        setSuccess('Market updates started')
        fetchStatus()
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(res.message || 'Failed to start')
      }
    } catch (err) {
      setError(err.message || 'Failed to start')
    } finally {
      setActionLoading(false)
    }
  }

  const handleStop = async () => {
    setActionLoading(true)
    setError(null)
    try {
      const res = await MarketSimulatorAPI.stop()
      if (res.success) {
        setSuccess('Market updates stopped')
        fetchStatus()
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(res.message || 'Failed to stop')
      }
    } catch (err) {
      setError(err.message || 'Failed to stop')
    } finally {
      setActionLoading(false)
    }
  }

  const handleResetDaily = async () => {
    setActionLoading(true)
    try {
      const res = await MarketSimulatorAPI.resetDaily()
      if (res.success) {
        setSuccess('Daily stats reset')
        onPriceUpdate?.()
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(res.message || 'Failed to reset')
      }
    } catch (err) {
      setError(err.message || 'Failed to reset')
    } finally {
      setActionLoading(false)
    }
  }

  const isRunning = status?.simulator?.isRunning

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2"
          >
            <X className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-sm">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2"
          >
            <Check className="w-4 h-4 text-emerald-400" />
            <p className="text-emerald-400 text-sm">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isRunning ? 'bg-emerald-500' : 'bg-slate-600'
          }`}>
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Market Updates</h3>
            <p className="text-xs text-slate-400">
              {isRunning ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Running - Each security uses its own mode (real/simulation)
                </span>
              ) : (
                'Stopped - Price updates paused'
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Market Stats */}
          {status?.marketStats && (
            <div className="hidden sm:flex items-center gap-3 mr-2 text-xs">
              <span className="text-slate-400">{status.marketStats.totalProducts || 0} securities</span>
              <span className="text-emerald-400">+{status.marketStats.gainers || 0}</span>
              <span className="text-red-400">-{status.marketStats.losers || 0}</span>
            </div>
          )}

          {isRunning ? (
            <button
              onClick={handleStop}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
              Stop
            </button>
          ) : (
            <button
              onClick={handleStart}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Start
            </button>
          )}

          <button
            onClick={handleResetDaily}
            disabled={actionLoading}
            className="p-2 bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
            title="Reset daily stats"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
